/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {ChangePasswordButton} from '@ion/dls/src/components/buttons/ChangePasswordButton';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import styles, {getClasses} from '@ion/dls/src/editors/editorStyles';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import {validatePassword} from '@ion/dls/src/utils/PasswordValidation';
import {useChangePassword} from '@ion/query/src/hooks/useChangePassword';
import {toggleUserPrompt} from '../../../store/actions/user-prompt';
import {changePassword as showChangePassword} from './state';
import {useAuthenticatedUser} from './useAuthenticatedUser';

const styleOverrides = (theme: DLSTheme) => ({
  formPaper : {
    width                          : '75%',
    [theme.breakpoints.down('sm')] : {
      maxWidth : '100%'
    }
  },
  formDialogContent : {
    [theme.breakpoints.down('sm')] : {
      paddingTop : '0px'
    }
  },
  chipContainer : {
    display   : 'flex',
    flexWrap  : 'wrap',
    maxHeight : '80px',
    overflowY : 'auto'
  },
  chipWrapper : {
    marginBottom : '10px',
    position     : 'relative'
  }
});

/**
 * The Change Password component provides the password change which is used for editing
 * the current user (i.e. the logged in user). This component must be accessible
 * throughout the application and not just from the Admin Users Page;
 * thus, it is only to be added once, and should only be added in the LayoutComponent.
 */
export const ChangePassword = (isPhrase: boolean = false) => {
  const editorStyles                                = useExtendStyles(styles, styleOverrides);
  const classes                                     = editorStyles();
  const dispatch                                    = useDispatch();
  const [currentPassword, setCurrentPassword]       = useState('');
  const [newPassword, setNewPassword]               = useState('');
  const [passwordConfirm, setPasswordConfirm]       = useState('');
  const [passwordError, setPasswordError]           = useState(null);
  const [isPasswordError, setIsPasswordError]       = useState<boolean>(false);
  const [passwordErrorValue, setPasswordErrorValue] = useState<boolean>(false);
  const {showDialog: showUserChangepassword}        = useSelector(({adminUsers: {changePassword}}) => changePassword);
  const {userPassword: userCurrentPassword}         = useSelector(({adminUsers: {savePassword}}) => savePassword);

  const disableUpdate = !newPassword || !passwordConfirm || passwordErrorValue || isPasswordError || passwordError;

  const currentUser   = useAuthenticatedUser();

  const {
    save,
    isSuccess,
    isError,
    isLoading
  } = useChangePassword(currentUser.id);

  useEffect(() => {
    if (userCurrentPassword) {
      setCurrentPassword(userCurrentPassword);
    } else {
      dispatch(showChangePassword(false));
    }
  }, []);

  const isValid = useCallback((passwordVal: string) => {
    const userValues = {
      config : {passwordMinLength: '10', passwordIsPhrase: isPhrase}
    };
    const validationResults = validatePassword(passwordVal, userValues);
    setPasswordError(validationResults || '');
    return isUndefined(validationResults);
  }, []);

  const onPasswordChange = useCallback(({target}: ChangeEvent<HTMLInputElement>) => {
    const {value} = target;
    const error = !isValid(value);
    setIsPasswordError(error);
    setNewPassword(value);
  }, []);

  const onPasswordConfirmChange = useCallback(({target}: ChangeEvent<HTMLInputElement>) => {
    const {value} = target;
    const error = (value !== newPassword);
    setPasswordConfirm(value);
    setPasswordErrorValue(error);
  }, [newPassword]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(showChangePassword(false));
      dispatch(toggleUserPrompt({message: translate('common:password.reset.success')}));
    }
    if (isError) {
      dispatch(toggleUserPrompt({message: translate('common:password.reset.currentPasswordError')}));
    }
  }, [isSuccess, isError]);

  const onCloseDialog = useCallback(() => {
    dispatch(showChangePassword(false));
  }, []);

  const onSave = useCallback(() => {
    if (currentPassword) {
      save(currentUser.id, {currentPassword: currentPassword, newPassword: passwordConfirm});
    } else {
      dispatch(showChangePassword(false));
    }
  }, [currentPassword, passwordConfirm]);

  return (
    <div>
      {showUserChangepassword && (
        <Dialog open={true}
          classes={getClasses(classes)}>
          <DialogTitle classes={{
            root : classes.formDialogTitle
          }}>
            <div className={classes.formTitle}>
              <div onClick={onCloseDialog} className={classes.closeButton}>
                <MaterialExitCloseIcon size={IconSize.medium}
                  color={dlsColor('grey3')} />
              </div>
              <Typography component='h1' variant='h2'>{translate('common:password.reset.title')}</Typography>
            </div>
          </DialogTitle>
          <form className={classes.form} noValidate autoComplete='off'>
            <div id={'editor'} className={classes.formWrapper}>
              <DialogContent className={classes.formDialogContent}>
                <FormSection title={translate('common:editors.shared.details',
                  {entityLabel: translate('common:entityLabels.user')})}
                bottomBorder>
                  <FormItem>
                    <FormTextField type='password'
                      fieldLabel={translate('common:editors.password.label')}
                      placeholder={translate('common:editors.password.placeholder')}
                      errorText={isPasswordError ? passwordError : ''}
                      onChange={onPasswordChange}
                      hasError={isPasswordError}
                      autoComplete='new-password'
                      name={'Password'}
                      value={newPassword}
                      maxLength={64} />
                  </FormItem>
                  <FormItem>
                    <FormTextField type='password'
                      fieldLabel={translate('common:editors.confirmPassword.label')}
                      placeholder={translate('common:editors.confirmPassword.placeholder')}
                      errorText={passwordErrorValue ? translate('common:password.reset.validation.error') : ''}
                      onChange={onPasswordConfirmChange}
                      hasError={passwordErrorValue}
                      autoComplete='confirm-password'
                      name={'ConfirmPassword'}
                      value={passwordConfirm}
                      maxLength={64} />
                  </FormItem>
                </FormSection>
              </DialogContent>
            </div>
          </form>
          <DialogActions className={classes.formActions}>
            <TertiaryButton onClick={onCloseDialog}>
              {translate('common:editors.shared.cancel')}
            </TertiaryButton>
            <div className={classes.primaryButton}>
              <ChangePasswordButton disabled={disableUpdate}
                onSave={onSave}
                loader={isLoading}
                label={translate('common:editors.shared.done')} />
            </div>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default React.memo(ChangePassword);
